import { CommonModule } from "@angular/common";
import { Component, computed, inject, signal } from "@angular/core";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { forkJoin, Observable } from "rxjs";
import { CourseService } from "src/app/services/api/courses.service";
import { SharedModule } from "src/shared/shared.module";
import { AuthService } from "../../auth";
import { StudentCourseItemComponent } from "../components/student-course-item/student-course-item.component";
import { environment } from "src/environments/environment";
import { PipesModule } from "src/utils/pipes/pipes.module";
import { ToastService } from "src/app/services/toast-service/toast.service";
import { TranslateModule, TranslateService } from "@ngx-translate/core";

@Component({
  selector: "app-dashboard-student-courses",
  imports: [
    SharedModule,
    CommonModule,
    RouterModule,
    StudentCourseItemComponent,
    PipesModule,
    TranslateModule,
  ],
  templateUrl: "./dashboard.component.html",
  styleUrl: "./dashboard.component.scss",
})
export class DashboardStudentCoursesComponent {
  #courseService = inject(CourseService);
  #authService = inject(AuthService);
  #translate = inject(TranslateService);
  #router = inject(Router);
  #toastService = inject(ToastService);

  _user: any;
  studentId = signal<number>(0);

  isActive = signal(false);
  isLoading = signal(true);

  studentCourses = signal<any[]>([]);

  /**
   * Filter over the enrolled courses.
   *
   * eazycal puts its course search on exactly this screen; the demo's own quiz
   * list has one two clicks away. Filtered here rather than on the server
   * because the whole list is already in hand — the request that populates this
   * page has no paging.
   */
  courseSearch = signal("");

  visibleCourses = computed(() => {
    const term = this.courseSearch().trim().toLowerCase();
    if (!term) return this.studentCourses();
    return this.studentCourses().filter((course) =>
      `${course?.course_name ?? ""} ${course?.course_category_name ?? ""}`
        .toLowerCase()
        .includes(term)
    );
  });

  onCourseSearch(value: string): void {
    this.courseSearch.set(value ?? "");
  }
  lastedCourses = signal<any>([]);

  fileUrl = environment.apiFileUrl;

  ngOnInit(): void {
    this.getCurrentUser();
    this.getStudentId();
    this.getMasterData();
  }

  getMasterData(): void {
    const params = {
      student_id: this.studentId(),
    };
    this._setLoading(true, 0);
    forkJoin([
      this.#courseService.getStudentCourse(params) as Observable<any>,
      this.#courseService.getStudentCourseLasted() as Observable<any>,
    ]).subscribe({
      next: ([studentCourses, lastedCourses]) => {
        this.studentCourses.set(studentCourses);
        this.lastedCourses.set(lastedCourses);
      },
      error: (err) => {},
      complete: () => {
        this._setLoading(false);
      },
    });
  }

  getStudentId(): void {
    this.studentId.set(this._user?.user.data.id);
  }

  getStudentCourse(): void {
    const params = {
      student_id: this.studentId(),
    };
    this.#courseService.getStudentCourse(params);
  }

  getLastedStudentCourse(): void {
    this.#courseService.getStudentCourseLasted(this.studentId());
  }

  getCurrentUser(): void {
    this._user = this.#authService.currentUser();
  }

  _setLoading(value: boolean, time: number = 500) {
    setTimeout(() => {
      this.isLoading.update((_) => value);
    }, time);
  }

  onActivateCourse(event: any): void {
    if (event) {
      this.getMasterData();
    }
  }

  enterLastedCourse() {
    const spentTime = this.lastedCourses().student_course.duration_spent;
    const durationExpire =
      this.lastedCourses().student_course.expire_duration_video;
    if (spentTime >= durationExpire) {
      this.#toastService.toastError(
        this.#translate.instant("ONLINE_CLASS.COURSE_TIMEOUT")
      );
    } else {
      this.#router.navigate(
        [`online-class/${this.lastedCourses().student_course_id}/watch`],
        {}
      );
    }
  }
}
